import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { auth, users as usersApi } from "../api/endpoints";
// feature/single-role-audit — multi-role aware via rolesOf().
import { rolesOf } from "../utils/permissions";

const Ctx = createContext(null);

// Apply data-theme attribute + localStorage cache so we don't flash light-on-load.
// feature/ui-polish-darkmode — when the theme actually CHANGES (not on boot),
// a temporary .theme-transition class animates background/color/border for
// 250ms so the switch cross-fades instead of snapping. Also keeps the
// browser-chrome <meta name="theme-color"> in sync.
let _themeTransitionTimer = null;
function applyTheme(theme) {
  const t = theme === "dark" ? "dark" : "light";
  const root = document.documentElement;
  const prev = root.getAttribute("data-theme");
  if (prev && prev !== t) {
    root.classList.add("theme-transition");
    clearTimeout(_themeTransitionTimer);
    _themeTransitionTimer = setTimeout(() => root.classList.remove("theme-transition"), 320);
  }
  root.setAttribute("data-theme", t);
  try { localStorage.setItem("eds.theme", t); } catch {}
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", t === "dark" ? "#1a1a1a" : "#FAFAF7");
}
function bootTheme() {
  try {
    const cached = localStorage.getItem("eds.theme");
    if (cached === "light" || cached === "dark") return cached;
  } catch {}
  if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  return "light";
}
// Apply at module load so the first paint is correct.
applyTheme(bootTheme());

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("eds.user")) || null; }
    catch { return null; }
  });
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!localStorage.getItem("eds.token")) { setBootstrapping(false); return; }
    auth.me()
      .then(r => {
        if (!alive) return;
        setUser(r.data);
        localStorage.setItem("eds.user", JSON.stringify(r.data));
        if (r.data.theme) applyTheme(r.data.theme);
      })
      .catch(() => { localStorage.removeItem("eds.token"); localStorage.removeItem("eds.user"); setUser(null); })
      .finally(() => alive && setBootstrapping(false));
    return () => { alive = false; };
  }, []);

  // Re-apply theme whenever user changes (e.g. login).
  useEffect(() => {
    if (user?.theme) applyTheme(user.theme);
  }, [user?.theme]);

  const login = useCallback(async (identifier, password) => {
    const r = await auth.login(identifier, password);
    localStorage.setItem("eds.token", r.data.token);
    localStorage.setItem("eds.user",  JSON.stringify(r.data.user));
    setUser(r.data.user);
    if (r.data.user.theme) applyTheme(r.data.user.theme);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await auth.logout(); } catch {}
    localStorage.removeItem("eds.token");
    localStorage.removeItem("eds.user");
    setUser(null);
  }, []);

  // Toggle/set theme — optimistic UI, server persists.
  const setTheme = useCallback(async (theme) => {
    const t = theme === "dark" ? "dark" : "light";
    applyTheme(t);
    setUser(u => {
      if (!u) return u;
      const next = { ...u, theme: t };
      localStorage.setItem("eds.user", JSON.stringify(next));
      return next;
    });
    try { await usersApi.updatePreferences({ theme: t }); }
    catch { /* persist failure is non-fatal — local change stays */ }
  }, []);

  // Re-fetch /me and update cached user. Used after preferences saves.
  const refreshUser = useCallback(async () => {
    try {
      const r = await auth.me();
      setUser(r.data);
      localStorage.setItem("eds.user", JSON.stringify(r.data));
      if (r.data.theme) applyTheme(r.data.theme);
    } catch { /* leave existing user in place */ }
  }, []);

  return <Ctx.Provider value={{ user, login, logout, bootstrapping, setTheme, refreshUser }}>{children}</Ctx.Provider>;
}

export function useAuth() { return useContext(Ctx); }

// Role helpers — auditor permissions are granted to reparto users (SPEC §2).
// feature/single-role-audit — multi-role aware via rolesOf() (imported at top).
export function isAuditor(user) {
  const roles = rolesOf(user);
  return roles.includes("reparto") || roles.includes("admin");
}
export function can(user, perm) {
  if (!user) return false;
  const roles = rolesOf(user);
  if (roles.includes("admin")) return true;
  const map = {
    create_case:   ["reparto"],
    delete_case:   [],
    insumos:       ["insumos"],
    reparto_insumos:["reparto_insumos"],
    revisor_fallos:["revisor_fallos"],
    abogado:       ["abogado"],
    auditor:       ["reparto"],
    admin:         [],
  };
  // feature/single-role-audit — match if any of the user's roles
  // appears in the permission's allowed-roles list.
  const allowed = map[perm] || [];
  return roles.some(r => allowed.includes(r));
}
